// Binary Search — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as stack.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import binarySearchDrills from './binarySearchDrills';
const drill = id => binarySearchDrills.find(d => d.id === id);

const binarySearch = {
  key: 'Binary Search',
  tagline: 'Every comparison throws away half of what\'s left — turning "check everything" into "check log n things."',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Binary Search',
      type: 'text',
      body: [
        "Binary search finds a target inside a SORTED range by repeatedly checking the middle element and discarding the half that can't possibly contain the answer. Every single comparison eliminates half of what remains — which is why doubling the input size only ever costs one extra comparison.",
        "The name suggests \"search an array,\" but the real idea is bigger: binary search works on ANY monotonic yes/no question over a range of candidates — not just array indices. That's what makes it show up in problems that don't look like searching at all, like \"what's the minimum speed that finishes in time?\" or \"what's the square root of this number?\"",
      ],
    },
    {
      id: 'why',
      title: 'Why Binary Search?',
      type: 'text',
      body: [
        "A linear scan costs O(n) — for a million elements, up to a million comparisons. Binary search costs O(log n) — for a million elements, at most about 20 comparisons. That gap only widens as the input grows, which is why binary search is one of the few algorithms where the complexity class itself, not just a constant factor, changes.",
        "The one requirement that makes this possible: the data (or the yes/no answer to some question about it) must be MONOTONIC — sorted, or at least consistently increasing/decreasing/one-directional in some sense. Without that, checking the middle tells you nothing about which half to discard.",
        "This is why binary search generalizes so far beyond \"find x in a sorted array\": first-and-last occurrence, insertion position, peak finding, and an entire family of \"binary search on the answer\" problems (minimum speed, minimum capacity, square roots) all reduce to the same halving idea.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Binary Search Works — Two Real-World Pictures',
      type: 'text',
      heading: 'Guessing a Cricket Score, and Tuning a Dial by Halves',
      body: [
        "Picture a friend thinking of a batsman's final score between 0 and 200, and you're only allowed to ask \"higher or lower?\" A bad strategy is guessing 1, then 2, then 3 — you might need 200 guesses. A good strategy is guessing 100 first: whatever the answer, you've just eliminated HALF the possibilities in one question. Guess the midpoint of what's left every time, and you'll nail the exact score in about 8 guesses, no matter how large the range was to start. That's binary search on an array: every \"higher or lower\" is a comparison, and the range of remaining possibilities is exactly [lo, hi].",
        "Now picture tuning an unfamiliar machine's speed dial to find the fastest setting that doesn't overheat it. You don't nudge the dial up by tiny increments from zero — you jump to the middle of the dial's range, run it, and check: did it overheat? If yes, the safe setting is somewhere in the lower half; if no, it might be even faster, so check the upper half. This is \"binary search on the answer\": there's no array here at all, just a range of candidate settings and a pass/fail check — exactly the shape of the Integer Square Root and \"minimum eating speed\" drills later in this course.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing lo, mid, and hi',
      type: 'pointer-diagram',
      body: [
        "Binary search tracks three positions at once: lo and hi mark the current boundaries of \"where the answer could still be,\" and mid is always the midpoint between them — the position actually being checked this step.",
        "The diagram below freezes one moment mid-search. Whatever the comparison at mid reveals, exactly one boundary moves inward next: either lo jumps past mid, or hi jumps just before it — the range of remaining possibilities always shrinks by roughly half.",
      ],
      demo: {
        values: [4, 9, 15, 22, 30, 41, 52, 61, 73],
        pointers: [
          { label: 'lo', index: 0, color: '#3B82F6' },
          { label: 'mid', index: 4, color: '#7F00FF' },
          { label: 'hi', index: 8, color: '#3B82F6' },
        ],
        note: 'mid = lo + (hi − lo) / 2 = 0 + (8 − 0) / 2 = 4. Comparing arr[4]=30 to the target decides whether lo jumps to 5 or hi jumps to 3.',
      },
    },
    {
      id: 'template',
      title: 'The Standard Binary Search Template',
      type: 'code',
      code:
`int lo = 0, hi = arr.length - 1;
while (lo <= hi) {
    int mid = lo + (hi - lo) / 2;
    if (arr[mid] == target) {
        return mid;
    } else if (arr[mid] < target) {
        lo = mid + 1;   // answer must be to the right
    } else {
        hi = mid - 1;    // answer must be to the left
    }
}
return -1;  // not found`,
      note: 'This exact shape — lo ≤ hi as the loop condition, mid = lo + (hi − lo) / 2, and moving exactly one boundary per iteration — is worth memorizing well enough to type without thinking.',
    },
    {
      id: 'overflow',
      title: 'Avoiding Overflow and Infinite Loops',
      type: 'code',
      code:
`// WRONG on very large arrays: lo + hi can overflow int's range
int mid = (lo + hi) / 2;

// CORRECT: mathematically identical, but never overflows
int mid = lo + (hi - lo) / 2;`,
      note: 'If lo and hi are both close to Integer.MAX_VALUE, lo + hi can silently wrap around to a negative number in Java — lo + (hi - lo) / 2 computes the exact same midpoint without ever adding two large numbers together.',
    },
    {
      id: 'boundvariants',
      title: 'Lower Bound and Upper Bound (Boundary Search)',
      type: 'code',
      code:
`// Find the FIRST index where arr[i] >= target (lower bound)
int lo = 0, hi = arr.length;
while (lo < hi) {
    int mid = lo + (hi - lo) / 2;
    if (arr[mid] < target) lo = mid + 1;
    else hi = mid;
}
// lo is now the lower bound`,
      note: 'Notice the differences from the classic template: hi starts at length (not length − 1), the loop condition is lo < hi (not <=), and the "found" branch is folded into hi = mid instead of returning immediately — this shape is what "find the first/last occurrence" and "search insert position" are both built from.',
    },
    {
      id: 'searchonanswer',
      title: 'Binary Search on the Answer',
      type: 'code',
      code:
`// Generic shape: search over POSSIBLE ANSWERS, not array indices
int lo = /* smallest possible answer */, hi = /* largest possible answer */;
int best = hi;
while (lo <= hi) {
    int mid = lo + (hi - lo) / 2;
    if (isFeasible(mid)) {   // some monotonic yes/no check
        best = mid;
        hi = mid - 1;         // try for something even better
    } else {
        lo = mid + 1;         // mid doesn't work — need more
    }
}
return best;`,
      note: 'There is no array being indexed here at all — lo and hi bound a range of candidate ANSWERS, and isFeasible replaces the array comparison. This is the single most powerful generalization of binary search, and it\'s what the Integer Square Root and "minimum eating speed" drills both use.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Running binary search on unsorted data',
          why: 'The entire technique depends on being able to discard a whole half based on one comparison — on unsorted data, that guarantee is gone, and the result is simply wrong with no warning.',
          fix: 'Sort the data first if the problem allows it, or confirm the "monotonic yes/no check" property actually holds before reaching for binary search on the answer.',
        },
        {
          mistake: 'Mixing up lo <= hi with lo < hi (and the matching mid update)',
          why: 'The classic "find an exact match" template uses lo <= hi with hi = mid - 1 / lo = mid + 1. The boundary-search template uses lo < hi with hi = mid (no minus one). Mixing the two styles causes infinite loops or off-by-one misses.',
          fix: 'Pick one template deliberately based on what\'s being searched for (an exact index vs. a boundary), and keep its loop condition and boundary updates consistent with each other.',
        },
        {
          mistake: 'Forgetting that hi = mid (without − 1) requires lo < hi, not lo <= hi',
          why: 'If hi is set to mid instead of mid − 1, but the loop still uses lo <= hi, the range can stop shrinking — lo and hi can end up equal forever, looping infinitely.',
          fix: 'Whenever a boundary update doesn\'t subtract or add 1 (hi = mid, lo = mid), the loop condition must exclude equality (lo < hi) so the range is still guaranteed to shrink.',
        },
        {
          mistake: 'Using (lo + hi) / 2 on very large ranges',
          why: 'If lo and hi are both large ints, their sum can overflow past Integer.MAX_VALUE and wrap around to a negative number, producing a nonsensical mid.',
          fix: 'Always compute mid as lo + (hi - lo) / 2 instead — same result, no overflow risk.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Binary search, sorted array', time: 'O(log n)', note: 'Each comparison discards half of what remains.' },
        { op: 'Linear scan (what binary search replaces)', time: 'O(n)', note: 'Every element checked in the worst case.' },
        { op: 'Binary search on the answer', time: 'O(log(range) · cost of feasibility check)', note: 'The range of candidate answers still halves each step; the check itself may cost more than O(1).' },
        { op: 'Sorting first (if data isn\'t already sorted)', time: 'O(n log n)', note: 'Only worth paying once if many searches follow — a single search doesn\'t justify sorting first.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for binary search when you see…',
      body: [
        'A sorted array (or a value that can cheaply be treated as sorted) and a search for a specific value.',
        'A request for the first, last, or boundary position where some condition starts or stops holding.',
        'A monotonic yes/no question over a RANGE OF POSSIBLE ANSWERS — "minimum speed/capacity/time that satisfies X" — even with no array in sight.',
        'A rotated or otherwise "almost sorted" array, where one half is still guaranteed to be properly ordered at every step.',
        'Avoid it when the data has no usable order and there\'s no monotonic property to exploit — that\'s when a full scan, or a different pattern entirely, is the honest answer.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('classic-binary-search'),
            drill('search-insert-position'),
            drill('first-bad-version'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('find-first-last-occurrence'),
            drill('find-peak-element'),
            drill('sqrt-x'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('search-rotated-sorted-array-drill'),
            drill('koko-eating-bananas'),
            drill('median-of-two-sorted-arrays-drill'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with the classic template, boundary search, and binary-search-on-the-answer before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Classic template: lo <= hi, mid = lo + (hi - lo) / 2, move exactly one boundary per iteration.',
        'Boundary-search template: lo < hi, hi = mid (no minus one) when the condition holds — used for first/last occurrence and insertion position.',
        'Binary search on the answer: lo/hi bound a range of candidate ANSWERS, not array indices — the check is a monotonic feasibility test, not an array comparison.',
        'Always compute mid as lo + (hi - lo) / 2, never (lo + hi) / 2, to avoid integer overflow.',
        'The one hard requirement: the data (or the yes/no check) must be monotonic — binary search silently gives wrong answers without that guarantee.',
        'O(log n) — the reason binary search remains fast even at massive scale, unlike a linear scan\'s O(n).',
        'A rotated sorted array still has one guaranteed-sorted half at every step — that\'s the key insight, not a special-cased exception.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default binarySearch;
